/**
 * useNavigation Hook
 *
 * Returns navigation groups filtered by user permissions and feature flags.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/stores/store';
import { usePermission } from './usePermission';
import { buildNavGroups, findActiveNavItem, isPathInModule } from '@/modules/utils/navigation';
import type { NavGroup, NavItem, FeatureFlagContext } from '@/modules/types';
import { useLocation } from 'react-router-dom';

/**
 * Default feature flag context that checks environment variables.
 */
const defaultFlagContext: FeatureFlagContext = {
  isEnabled: (flagName: string): boolean => {
    // Check environment variable
    const envValue = import.meta.env[`VITE_${flagName}`];
    return envValue === 'true' || envValue === '1';
  },
};

/**
 * Hook to get navigation groups for the sidebar.
 */
export function useNavigation(
  flagContext: FeatureFlagContext = defaultFlagContext
): NavGroup[] {
  const { t } = useTranslation();
  const { ability, isLoading } = usePermission();
  const { conversationsStore } = useStore();

  const userContext = useMemo(() => ({
    ability,
    isLoading,
  }), [ability, isLoading]);

  // Notification state getter for conversations
  const getNotificationState = useMemo(() => {
    return (id: string): boolean => {
      if (id === 'conversations') {
        return conversationsStore.conversationsWithMessages.size > 0;
      }
      return false;
    };
  }, [conversationsStore.conversationsWithMessages.size]);

  const navGroups = useMemo(() => {
    return buildNavGroups(userContext, t, flagContext, getNotificationState);
  }, [userContext, t, flagContext, getNotificationState]);

  return navGroups;
}

/**
 * Hook to get the currently active navigation item.
 */
export function useActiveNavItem(): NavItem | undefined {
  const location = useLocation();
  const navGroups = useNavigation();

  return useMemo(() => {
    return findActiveNavItem(location.pathname, navGroups);
  }, [location.pathname, navGroups]);
}

/**
 * Hook to check if current path is within a specific module.
 */
export function useIsInModule(moduleId: string): boolean {
  const location = useLocation();

  return useMemo(() => {
    return isPathInModule(location.pathname, moduleId);
  }, [location.pathname, moduleId]);
}

export default useNavigation;
